import type { MindmapNode, MindmapNodeShape } from '@/features/mindmap/core/MindmapTypes';
import type { MindmapDirection } from './Ast';

type BorderStyle = 'solid' | 'dashed' | 'dotted';

type ColorPalette = Record<string, string>;
type ColorEntry = ColorPalette | string;

export interface CascadingStyle {
  bgColor?: string;
  textColor?: string;
  borderColor?: string;
  borderStyle?: BorderStyle;
  borderWidth?: number;
  boxShadow?: string;
  shape: MindmapNodeShape;
}

export interface LocalStyle {
  bgColor?: string;
  textColor?: string;
  borderColor?: string;
  borderStyle?: BorderStyle;
  borderWidth?: number;
  fontSize?: string;
  lineHeight?: string;
  boxShadow?: string;
  borderRadius?: string;
}

export interface TokenResolution {
  applied: CascadingStyle & LocalStyle;
  nextContext: CascadingStyle;
  direction?: MindmapDirection;
}

/**
 * Tailwind color palette with hex values.
 * Note: We use a static hex map instead of importing from tailwindcss/colors
 * because Tailwind v4 returns OKLCH colors which break hex-based token derivation.
 */
const TAILWIND_HEX_COLORS: Record<string, Record<string, string>> = {
  slate: {
    50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8',
    500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a', 950: '#020617',
  },
  gray: {
    50: '#f9fafb', 100: '#f3f4f6', 200: '#e5e7eb', 300: '#d1d5db', 400: '#9ca3af',
    500: '#6b7280', 600: '#4b5563', 700: '#374151', 800: '#1f2937', 900: '#111827', 950: '#030712',
  },
  zinc: {
    50: '#fafafa', 100: '#f4f4f5', 200: '#e4e4e7', 300: '#d4d4d8', 400: '#a1a1aa',
    500: '#71717a', 600: '#52525b', 700: '#3f3f46', 800: '#27272a', 900: '#18181b', 950: '#09090b',
  },
  neutral: {
    50: '#fafafa', 100: '#f5f5f5', 200: '#e5e5e5', 300: '#d4d4d4', 400: '#a3a3a3',
    500: '#737373', 600: '#525252', 700: '#404040', 800: '#262626', 900: '#171717', 950: '#0a0a0a',
  },
  stone: {
    50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e',
    500: '#78716c', 600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917', 950: '#0c0a09',
  },
  red: {
    50: '#fef2f2', 100: '#fee2e2', 200: '#fecaca', 300: '#fca5a5', 400: '#f87171',
    500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b', 900: '#7f1d1d', 950: '#450a0a',
  },
  orange: {
    50: '#fff7ed', 100: '#ffedd5', 200: '#fed7aa', 300: '#fdba74', 400: '#fb923c',
    500: '#f97316', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412', 900: '#7c2d12', 950: '#431407',
  },
  amber: {
    50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d', 400: '#fbbf24',
    500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e', 900: '#78350f', 950: '#451a03',
  },
  yellow: {
    50: '#fefce8', 100: '#fef9c3', 200: '#fef08a', 300: '#fde047', 400: '#facc15',
    500: '#eab308', 600: '#ca8a04', 700: '#a16207', 800: '#854d0e', 900: '#713f12', 950: '#422006',
  },
  lime: {
    50: '#f7fee7', 100: '#ecfccb', 200: '#d9f99d', 300: '#bef264', 400: '#a3e635',
    500: '#84cc16', 600: '#65a30d', 700: '#4d7c0f', 800: '#3f6212', 900: '#365314', 950: '#1a2e05',
  },
  green: {
    50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 300: '#86efac', 400: '#4ade80',
    500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534', 900: '#14532d', 950: '#052e16',
  },
  emerald: {
    50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 400: '#34d399',
    500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b', 950: '#022c22',
  },
  teal: {
    50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4', 400: '#2dd4bf',
    500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59', 900: '#134e4a', 950: '#042f2e',
  },
  cyan: {
    50: '#ecfeff', 100: '#cffafe', 200: '#a5f3fc', 300: '#67e8f9', 400: '#22d3ee',
    500: '#06b6d4', 600: '#0891b2', 700: '#0e7490', 800: '#155e75', 900: '#164e63', 950: '#083344',
  },
  sky: {
    50: '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd', 300: '#7dd3fc', 400: '#38bdf8',
    500: '#0ea5e9', 600: '#0284c7', 700: '#0369a1', 800: '#075985', 900: '#0c4a6e', 950: '#082f49',
  },
  blue: {
    50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd', 400: '#60a5fa',
    500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af', 900: '#1e3a8a', 950: '#172554',
  },
  indigo: {
    50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc', 400: '#818cf8',
    500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3', 900: '#312e81', 950: '#1e1b4b',
  },
  violet: {
    50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd', 400: '#a78bfa',
    500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6', 900: '#4c1d95', 950: '#2e1065',
  },
  purple: {
    50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe', 400: '#c084fc',
    500: '#a855f7', 600: '#9333ea', 700: '#7e22ce', 800: '#6b21a8', 900: '#581c87', 950: '#3b0764',
  },
  fuchsia: {
    50: '#fdf4ff', 100: '#fae8ff', 200: '#f5d0fe', 300: '#f0abfc', 400: '#e879f9',
    500: '#d946ef', 600: '#c026d3', 700: '#a21caf', 800: '#86198f', 900: '#701a75', 950: '#4a044e',
  },
  pink: {
    50: '#fdf2f8', 100: '#fce7f3', 200: '#fbcfe8', 300: '#f9a8d4', 400: '#f472b6',
    500: '#ec4899', 600: '#db2777', 700: '#be185d', 800: '#9d174d', 900: '#831843', 950: '#500724',
  },
  rose: {
    50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af', 400: '#fb7185',
    500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239', 900: '#881337', 950: '#4c0519',
  },
};

const COLOR_KEYS = Object.keys(TAILWIND_HEX_COLORS);

const COLOR_MAP: Record<string, ColorEntry> = {
  black: '#000000',
  white: '#ffffff',
  ...TAILWIND_HEX_COLORS,
};

const COLOR_NAME_SET = new Set<string>([...COLOR_KEYS, 'black', 'white']);

const DEFAULT_COLOR_SHADE = '500';
const DEFAULT_BORDER_COLOR = '#d1d5db';

const SHAPE_TOKENS: Record<string, MindmapNodeShape> = {
  '#rounded': 'rounded',
  '#pill': 'pill',
  '#square': 'rectangle',
  '#underline': 'underline',
  '#line': 'underline',
};

const BORDER_STYLE_TOKENS: Record<string, BorderStyle> = {
  '#border-solid': 'solid',
  '#border-dashed': 'dashed',
  '#border-dotted': 'dotted',
};

const DIRECTION_TOKENS: Record<string, MindmapDirection> = {
  '#dir-left': 'left',
  '#dir-right': 'right',
  '#dir-both': 'both',
};

const TEXT_SIZE_MAP: Record<string, { fontSize: string; lineHeight: string }> = {
  '#text-xs': { fontSize: '0.75rem', lineHeight: '1rem' },
  '#text-sm': { fontSize: '0.875rem', lineHeight: '1.25rem' },
  '#text-base': { fontSize: '1rem', lineHeight: '1.5rem' },
  '#text-lg': { fontSize: '1.125rem', lineHeight: '1.75rem' },
  '#text-xl': { fontSize: '1.25rem', lineHeight: '1.75rem' },
  '#text-2xl': { fontSize: '1.5rem', lineHeight: '2rem' },
  '#text-3xl': { fontSize: '1.875rem', lineHeight: '2.25rem' },
  '#text-4xl': { fontSize: '2.25rem', lineHeight: '2.5rem' },
  '#text-5xl': { fontSize: '3rem', lineHeight: '1' },
  '#text-6xl': { fontSize: '3.75rem', lineHeight: '1' },
  '#text-7xl': { fontSize: '4.5rem', lineHeight: '1' },
  '#text-8xl': { fontSize: '6rem', lineHeight: '1' },
  '#text-9xl': { fontSize: '8rem', lineHeight: '1' },
};

const SHADOW_MAP: Record<string, string> = {
  '#shadow': '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
  '#shadow-sm': '0 1px 2px 0 rgba(0,0,0,0.05)',
  '#shadow-md': '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
  '#shadow-lg': '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
  '#shadow-xl': '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
  '#shadow-none': 'none',
};

const ROUNDED_MAP: Record<string, string> = {
  '#rounded-none': '0px',
  '#rounded-sm': '0.125rem',
  '#rounded': '0.25rem',
  '#rounded-md': '0.375rem',
  '#rounded-lg': '0.5rem',
  '#rounded-xl': '0.75rem',
  '#rounded-2xl': '1rem',
  '#rounded-3xl': '1.5rem',
  '#rounded-full': '9999px',
};

const BORDER_WIDTH_PATTERN = /^#border-(\d+)$/;

const COLOR_TOKEN_PATTERN = /^#?([a-z]+)(?:-(\d{2,3}))?$/;

const BG_TOKEN_PATTERN = /^#bg-([a-z]+)(?:-(\d{2,3}))?$/;
const TEXT_COLOR_TOKEN_PATTERN = /^#text-([a-z]+)(?:-(\d{2,3}))?$/;
const BORDER_COLOR_TOKEN_PATTERN = /^#border-([a-z]+)(?:-(\d{2,3}))?$/;

const cloneContext = (context: CascadingStyle): CascadingStyle => ({
  bgColor: context.bgColor,
  textColor: context.textColor,
  borderColor: context.borderColor,
  borderStyle: context.borderStyle,
  borderWidth: context.borderWidth,
  boxShadow: context.boxShadow,
  shape: context.shape,
});

const resolveColor = (colorKey: string, shade?: string): string | undefined => {
  const key = colorKey.toLowerCase();
  const entry = COLOR_MAP[key];
  if (!entry) return undefined;

  if (typeof entry === 'string') return entry;

  if (!shade) {
    if (entry[DEFAULT_COLOR_SHADE]) return entry[DEFAULT_COLOR_SHADE];
    if (entry.DEFAULT) return entry.DEFAULT;
    return undefined;
  }

  return entry[shade] ?? entry.DEFAULT ?? entry[DEFAULT_COLOR_SHADE];
};

// Text color should cascade (like CSS inheritance)
const applyCascadedTextColor = (
  applied: CascadingStyle & LocalStyle,
  nextContext: CascadingStyle,
  color: string
) => {
  nextContext.textColor = color;
  applied.textColor = color;
};

// Background color tokens (e.g., #blue) apply only to the current node.
// Children fall back to their own styles or the global default instead of inheriting.
const applyCascadedColor = (
  applied: CascadingStyle & LocalStyle,
  _nextContext: CascadingStyle,
  color: string
) => {
  applied.bgColor = color;
};

// Border properties should NOT cascade - only affect the node they're applied to
const applyBorderColor = (
  applied: CascadingStyle & LocalStyle,
  color: string
) => {
  applied.borderColor = color;
};

const applyBorderStyle = (
  applied: CascadingStyle & LocalStyle,
  style: BorderStyle
) => {
  applied.borderStyle = style;
};

const applyBorderWidth = (
  applied: CascadingStyle & LocalStyle,
  width: number
) => {
  applied.borderWidth = width;
};

// Shadow should NOT cascade
const applyShadow = (
  applied: CascadingStyle & LocalStyle,
  shadow: string
) => {
  applied.boxShadow = shadow;
};

// Shape should NOT cascade - each node should have explicit shape
const applyShape = (
  applied: CascadingStyle & LocalStyle,
  nextContext: CascadingStyle,
  shape: MindmapNodeShape
) => {
  // Still update nextContext.shape for default fallback, but not as cascading inheritance
  nextContext.shape = shape;
  applied.shape = shape;
};

export function resolveNodeStyles(tokens: string[], parentContext: CascadingStyle): TokenResolution {
  const nextContext = cloneContext(parentContext);
  const applied: CascadingStyle & LocalStyle = cloneContext(parentContext);
  let direction: MindmapDirection | undefined;

  for (const rawToken of tokens) {
    const token = rawToken.trim();
    if (!token.length || !token.startsWith('#')) continue;
    const lower = token.toLowerCase();

    if (DIRECTION_TOKENS[lower]) {
      direction = DIRECTION_TOKENS[lower];
      continue;
    }

    if (SHAPE_TOKENS[lower]) {
      applyShape(applied, nextContext, SHAPE_TOKENS[lower]);
      continue;
    }

    if (lower === '#border') {
      applyBorderColor(applied, nextContext.borderColor ?? DEFAULT_BORDER_COLOR);
      applyBorderStyle(applied, 'solid');
      applyBorderWidth(applied, nextContext.borderWidth ?? 1);
      continue;
    }

    if (BORDER_STYLE_TOKENS[lower]) {
      applyBorderStyle(applied, BORDER_STYLE_TOKENS[lower]);
      continue;
    }

    const widthMatch = lower.match(BORDER_WIDTH_PATTERN);
    if (widthMatch) {
      const width = parseInt(widthMatch[1], 10);
      if (!Number.isNaN(width)) {
        applyBorderWidth(applied, width);
        continue;
      }
    }

    if (ROUNDED_MAP[lower]) {
      applied.borderRadius = ROUNDED_MAP[lower];
      continue;
    }

    if (SHADOW_MAP[lower]) {
      applyShadow(applied, SHADOW_MAP[lower]);
      continue;
    }

    const bgMatch = lower.match(BG_TOKEN_PATTERN);
    if (bgMatch) {
      const color = resolveColor(bgMatch[1], bgMatch[2]);
      if (color) applied.bgColor = color;
      continue;
    }

    const textSize = TEXT_SIZE_MAP[lower];
    if (textSize) {
      applied.fontSize = textSize.fontSize;
      applied.lineHeight = textSize.lineHeight;
      continue;
    }

    const textColorMatch = lower.match(TEXT_COLOR_TOKEN_PATTERN);
    if (textColorMatch) {
      const color = resolveColor(textColorMatch[1], textColorMatch[2]);
      if (color) applyCascadedTextColor(applied, nextContext, color);
      continue;
    }

    const borderColorMatch = lower.match(BORDER_COLOR_TOKEN_PATTERN);
    if (borderColorMatch) {
      const color = resolveColor(borderColorMatch[1], borderColorMatch[2]);
      if (color) applyBorderColor(applied, color);
      continue;
    }

    const colorMatch = lower.match(COLOR_TOKEN_PATTERN);
    if (colorMatch) {
      const color = resolveColor(colorMatch[1], colorMatch[2]);
      if (color) {
        applyCascadedColor(applied, nextContext, color);
      }
      continue;
    }
  }

  applied.shape = applied.shape ?? nextContext.shape;

  return { applied, nextContext, direction };
}

const normaliseHex = (value: string) => {
  if (!value) return '';
  const trimmed = value.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return trimmed;
};

const SHADOW_REVERSE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(SHADOW_MAP).map(([token, value]) => [value, token])
);

const ROUNDED_REVERSE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(ROUNDED_MAP).map(([token, value]) => [value, token])
);

const TEXT_SIZE_REVERSE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(TEXT_SIZE_MAP).map(([token, metrics]) => [`${metrics.fontSize}|${metrics.lineHeight}`, token])
);

const isColorAliasToken = (token: string) => {
  if (!token.startsWith('#')) return false;
  const body = token.slice(1);
  const segments = body.split('-');
  if (segments.length === 1) return COLOR_NAME_SET.has(segments[0]);
  if (segments.length === 2 && COLOR_NAME_SET.has(segments[0]) && /^\d{2,3}$/.test(segments[1])) {
    return true;
  }
  return false;
};

const isManagedToken = (token: string) => {
  if (!token.startsWith('#')) return false;
  if (token.startsWith('#dir-')) return false;
  if (token === '#line' || token === '#underline' || token === '#pill' || token === '#square') return true;
  if (token.startsWith('#bg-')) return true;
  if (token.startsWith('#text-')) return true;
  if (token.startsWith('#border')) return true;
  if (token.startsWith('#shadow')) return true;
  if (token.startsWith('#rounded')) return true;
  if (isColorAliasToken(token)) return true;
  return false;
};

export function colorHexToToken(hex: string, domain: 'bg' | 'text' | 'border'): string | null {
  const normalised = normaliseHex(hex);
  if (!normalised) return null;

  if (normalised === COLOR_MAP.black) {
    if (domain === 'bg') return '#black';
    return `#${domain}-black`;
  }
  if (normalised === COLOR_MAP.white) {
    if (domain === 'bg') return '#white';
    return `#${domain}-white`;
  }

  for (const [name, entry] of Object.entries(COLOR_MAP)) {
    if (typeof entry === 'string') continue;
    for (const [shade, value] of Object.entries(entry)) {
      if (normaliseHex(value) === normalised) {
        if (domain === 'bg') {
          if (shade === DEFAULT_COLOR_SHADE) return `#${name}`;
          return `#bg-${name}-${shade}`;
        }
        return `#${domain}-${name}-${shade}`;
      }
    }
  }

  return null;
}

export function deriveStyleTokensFromNode(node: MindmapNode): string[] {
  const managedTokens: string[] = [];

  const addToken = (token?: string | null) => {
    if (!token) return;
    if (!managedTokens.includes(token)) managedTokens.push(token);
  };

  switch (node.shape) {
    case 'pill':
      addToken('#pill');
      break;
    case 'rectangle':
      addToken('#square');
      break;
    case 'underline':
      addToken('#line');
      break;
    default:
      break;
  }

  if (node.bgColor) {
    addToken(colorHexToToken(node.bgColor, 'bg'));
  }

  if (node.textColor) {
    addToken(colorHexToToken(node.textColor, 'text'));
  }

  if (node.fontSize && node.lineHeight) {
    const sizeToken = TEXT_SIZE_REVERSE_MAP[`${node.fontSize}|${node.lineHeight}`];
    addToken(sizeToken);
  }

  if (node.borderRadius) {
    addToken(ROUNDED_REVERSE_MAP[node.borderRadius]);
  }

  let hasBorderToken = false;

  if (node.borderStyle) {
    if (node.borderStyle === 'dashed') addToken('#border-dashed');
    else if (node.borderStyle === 'dotted') addToken('#border-dotted');
    else addToken('#border');
    hasBorderToken = true;
  }

  if (typeof node.borderWidth === 'number' && Number.isFinite(node.borderWidth)) {
    addToken(`#border-${Math.round(node.borderWidth)}`);
    hasBorderToken = true;
  }

  if (node.borderColor) {
    addToken(colorHexToToken(node.borderColor, 'border'));
    hasBorderToken = true;
  }

  if (!hasBorderToken && (node.borderColor || node.borderWidth != null || node.borderStyle)) {
    addToken('#border');
  }

  if (node.boxShadow) {
    addToken(SHADOW_REVERSE_MAP[node.boxShadow]);
  }

  const existing = node.styleTokens ?? [];
  const extras = existing.filter((token) => !isManagedToken(token));

  const combined = [...extras, ...managedTokens];
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const token of combined) {
    if (!token) continue;
    if (seen.has(token)) continue;
    seen.add(token);
    deduped.push(token);
  }
  return deduped;
}
